import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Loader2, FileText, Copy, Trash2, Check, Search, Sparkles, Info } from 'lucide-react';
import { api } from '../../lib/api';
import type { MediaItem } from '../../lib/types';
import ContextCard from '../../components/ui/ContextCard';
import PageContainer from '../../components/ui/PageContainer';
import PageHeader from '../../components/ui/PageHeader';
import Modal from '../../components/ui/Modal';
import { BLOCK_CATALOG } from '@palacete/shared/blocks';

const BLOCK_LABELS: Record<string, string> = Object.fromEntries(
  BLOCK_CATALOG.map((b) => [b.type, b.label]),
);

type OriginFilter = 'all' | 'upload' | 'ai-generated';

interface MediaMeta {
  model?: string;
  purpose?: string;
  blockType?: string;
  fieldKey?: string;
  variant?: number;
}

function parseMeta(raw: string): MediaMeta {
  try { return JSON.parse(raw) as MediaMeta; } catch { return {}; }
}

const isAi = (item: MediaItem) => item.source === 'ai-generated';

type MediaFolder = 'properties' | 'agents' | 'posts' | 'settings' | 'pages' | 'general';

const FOLDER_LABELS: Record<string, string> = {
  pages: 'Páginas',
  properties: 'Propiedades',
  agents: 'Agentes',
  posts: 'Blog',
  settings: 'Ajustes',
  general: 'General',
};

const FOLDERS: MediaFolder[] = ['general', 'pages', 'properties', 'agents', 'posts', 'settings'];

function toWebP(file: File, folder: string, quality = 0.85): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml' || folder === 'settings') return Promise.resolve(file);
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d')!.drawImage(img, 0, 0);
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Canvas toBlob failed'));
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' }));
        },
        'image/webp',
        quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
    img.src = url;
  });
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MediaPage() {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadFolder, setUploadFolder] = useState<MediaFolder>('general');
  const [folderFilter, setFolderFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [originFilter, setOriginFilter] = useState<OriginFilter>('all');
  const [detailsItem, setDetailsItem] = useState<MediaItem | null>(null);
  const [imageUsage, setImageUsage] = useState<{ used: number; limit: number; allowed: boolean } | null>(null);

  useEffect(() => {
    api.media.list().then(setItems).catch(console.error).finally(() => setLoading(false));
    api.ai.imageUsage().then(setImageUsage).catch(() => { /* sin uso visible si falla */ });
  }, []);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setUploading(true);
    setUploadError(null);
    try {
      const uploaded = await Promise.all(
        acceptedFiles.map(file => toWebP(file, uploadFolder).then(f => api.media.upload(f, uploadFolder)))
      );
      setItems(prev => [...(uploaded as unknown as MediaItem[]), ...prev]);
    } catch {
      setUploadError('Error al subir. Intenta de nuevo.');
    } finally {
      setUploading(false);
    }
  }, [uploadFolder]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [], 'application/pdf': [] },
    multiple: true,
  });

  async function handleDelete(id: number, filename: string) {
    if (!confirm(`¿Eliminar "${filename}"?`)) return;
    await api.media.delete(id);
    setItems(prev => prev.filter(m => m.id !== id));
  }

  async function handleCopy(item: MediaItem) {
    await navigator.clipboard.writeText(item.url);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  const availableFolders = [...new Set(items.map(item => item.r2Key.split('/')[1] ?? 'general'))].sort();

  const aiCount = items.filter(isAi).length;

  const filtered = items.filter(item => {
    if (folderFilter !== 'all' && item.r2Key.split('/')[1] !== folderFilter) return false;
    if (originFilter === 'ai-generated' && !isAi(item)) return false;
    if (originFilter === 'upload' && isAi(item)) return false;
    if (search) return item.filename.toLowerCase().includes(search.toLowerCase());
    return true;
  });

  return (
    <PageContainer>
      <PageHeader title="Biblioteca de archivos e imágenes" />

      {imageUsage?.allowed && (
        <div className="flex items-center gap-2 text-sm text-ui-text-muted">
          <Sparkles size={15} className="text-ui-accent" />
          <span>
            Imágenes creadas con IA este mes: <strong className="text-ui-text">{imageUsage.used}</strong> de {imageUsage.limit}
          </span>
        </div>
      )}

      <ContextCard
        title="¿Qué es la biblioteca de archivos e imágenes?"
        description="Es el lugar donde guardas las imágenes y documentos que después puedes usar en páginas, publicaciones y otras secciones del sitio."
        detail="La carpeta destino solo ayuda a mantener los archivos ordenados. El archivo no aparecerá en el sitio hasta que lo selecciones dentro de algún contenido y publiques los cambios."
      />

      {/* Upload zone */}
      <div className="bg-ui-surface border border-ui-border rounded-xl p-5 space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium text-ui-text">Subir archivos</p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-ui-text-subtle shrink-0">Guardar en:</span>
            <div className="flex gap-1 flex-wrap">
              {FOLDERS.map(f => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setUploadFolder(f)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    uploadFolder === f
                      ? 'bg-ui-accent text-ui-text-inverse'
                      : 'bg-ui-surface text-ui-text-muted hover:text-ui-text hover:bg-ui-surface-2'
                  }`}
                >
                  {FOLDER_LABELS[f]}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          {...getRootProps()}
          className={`
            flex items-center gap-3 border-2 border-dashed rounded-lg px-4 py-4 cursor-pointer transition-colors
            ${isDragActive ? 'border-ui-accent bg-ui-accent/10' : 'border-ui-border hover:border-ui-border-strong'}
            ${uploading ? 'pointer-events-none opacity-50' : ''}
          `}
        >
          <input {...getInputProps()} />
          {uploading
            ? <Loader2 size={20} className="animate-spin text-ui-accent flex-shrink-0" />
            : <Upload size={20} className="text-ui-text-muted flex-shrink-0" />
          }
          <div>
            <p className="text-sm text-ui-text">
              {uploading ? 'Subiendo...' : isDragActive ? 'Suelta aquí' : 'Arrastra archivos o haz clic para seleccionar'}
            </p>
            <p className="text-sm text-ui-text-subtle">
              Imágenes (JPG, PNG, WebP, SVG) o PDF · máx. 10 MB · las imágenes se convierten a WebP
            </p>
          </div>
        </div>
        {uploadError && <p className="text-sm text-ui-danger">{uploadError}</p>}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Folder pills */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setFolderFilter('all')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              folderFilter === 'all'
                ? 'bg-ui-accent text-ui-text-inverse'
                : 'bg-ui-surface text-ui-text-muted hover:text-ui-text hover:bg-ui-surface-2'
            }`}
          >
            Todos {items.length > 0 && <span className="opacity-60">({items.length})</span>}
          </button>
          {availableFolders.map(f => {
            const count = items.filter(i => i.r2Key.split('/')[1] === f).length;
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFolderFilter(f)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  folderFilter === f
                    ? 'bg-ui-accent text-ui-text-inverse'
                    : 'bg-ui-surface text-ui-text-muted hover:text-ui-text hover:bg-ui-surface-2'
                }`}
              >
                {FOLDER_LABELS[f] ?? f} <span className="opacity-60">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Origin pills — solo si hay assets creados con IA */}
        {aiCount > 0 && (
          <div className="flex items-center gap-2">
            {([
              ['all', 'Todos'],
              ['upload', 'Subidas'],
              ['ai-generated', 'Creadas con IA'],
            ] as [OriginFilter, string][]).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setOriginFilter(value)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  originFilter === value
                    ? 'bg-ui-accent text-ui-text-inverse'
                    : 'bg-ui-surface text-ui-text-muted hover:text-ui-text hover:bg-ui-surface-2'
                }`}
              >
                {label}
                {value === 'ai-generated' && <span className="opacity-60"> ({aiCount})</span>}
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="relative sm:ml-auto sm:w-64">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ui-text-subtle" />
          <input
            type="text"
            placeholder="Buscar por nombre..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-ui-surface border border-ui-border rounded-lg pl-8 pr-3 py-1.5 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent"
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={28} className="animate-spin text-ui-text-subtle" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-ui-text-subtle">
          <p className="text-sm">{search || folderFilter !== 'all' ? 'Sin resultados' : 'Sin archivos aún'}</p>
          {!search && folderFilter === 'all' && <p className="text-sm mt-1">Sube el primer archivo usando la zona de arriba</p>}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {filtered.map(item => (
            <div
              key={item.id}
              className="group relative bg-ui-surface rounded-lg border border-ui-border overflow-hidden"
            >
              {item.mimeType.startsWith('image/') ? (
                <img src={item.url} alt={item.filename} className="w-full aspect-square object-cover" />
              ) : (
                <div className="w-full aspect-square flex flex-col items-center justify-center gap-2 text-ui-text-muted bg-ui-surface/50">
                  <FileText size={28} />
                  <span className="text-xs text-ui-text-subtle px-2 text-center leading-tight line-clamp-2">{item.filename}</span>
                </div>
              )}

              {/* Folder badge */}
              <div className="absolute top-1.5 left-1.5">
                <span className="text-xs font-medium bg-black/55 text-white px-1.5 py-0.5 rounded backdrop-blur-sm">
                  {FOLDER_LABELS[item.r2Key.split('/')[1]] ?? item.r2Key.split('/')[1]}
                </span>
              </div>

              {/* AI badge */}
              {isAi(item) && (
                <div className="absolute top-1.5 right-1.5">
                  <span className="flex items-center gap-1 text-xs font-medium bg-ui-accent/90 text-ui-text-inverse px-1.5 py-0.5 rounded backdrop-blur-sm">
                    <Sparkles size={11} /> IA
                  </span>
                </div>
              )}

              {/* Info bar */}
              <div className="px-2 py-1.5 border-t border-ui-border bg-ui-surface">
                <p className="text-xs text-ui-text-muted truncate">{item.filename}</p>
                {item.sizeBytes && <p className="text-xs text-ui-text-subtle">{formatBytes(item.sizeBytes)}</p>}
              </div>

              {/* Hover actions */}
              <div className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 backdrop-blur-[1px]">
                {isAi(item) && (
                  <button
                    onClick={() => setDetailsItem(item)}
                    title="Ver detalles"
                    className="p-2 bg-white/15 hover:bg-white/25 text-white rounded-lg transition-colors"
                  >
                    <Info size={16} />
                  </button>
                )}
                <button
                  onClick={() => handleCopy(item)}
                  title="Copiar URL"
                  className="p-2 bg-white/15 hover:bg-white/25 text-white rounded-lg transition-colors"
                >
                  {copiedId === item.id ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                </button>
                <button
                  onClick={() => handleDelete(item.id, item.filename)}
                  title="Eliminar"
                  className="p-2 bg-ui-danger/80 hover:bg-ui-danger text-ui-text-inverse rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {detailsItem && (
        <Modal title="Detalles de la imagen" onClose={() => setDetailsItem(null)}>
          {(() => {
            const meta = parseMeta(detailsItem.metaJson);
            const origin = meta.blockType ? (BLOCK_LABELS[meta.blockType] ?? meta.blockType) : null;
            return (
              <div className="space-y-4 pb-2">
                <img
                  src={detailsItem.url}
                  alt={detailsItem.altText ?? detailsItem.filename}
                  className="w-full rounded-lg border border-ui-border object-contain max-h-72 bg-ui-surface-2"
                />
                <dl className="space-y-3 text-sm">
                  <div className="flex items-center gap-2 text-ui-accent">
                    <Sparkles size={15} />
                    <span className="font-medium">Creada con IA</span>
                  </div>
                  {detailsItem.altText && (
                    <div>
                      <dt className="text-ui-text-subtle">Descripción</dt>
                      <dd className="text-ui-text">{detailsItem.altText}</dd>
                    </div>
                  )}
                  {origin && (
                    <div>
                      <dt className="text-ui-text-subtle">Dónde se usó</dt>
                      <dd className="text-ui-text">Bloque “{origin}”</dd>
                    </div>
                  )}
                  {meta.model && (
                    <div>
                      <dt className="text-ui-text-subtle">Generada con</dt>
                      <dd className="text-ui-text">{meta.model}</dd>
                    </div>
                  )}
                </dl>
                <button
                  type="button"
                  onClick={() => handleCopy(detailsItem)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-ui-surface-2 text-ui-text-muted hover:text-ui-text text-sm transition-colors"
                >
                  {copiedId === detailsItem.id ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                  Copiar enlace para reutilizar
                </button>
              </div>
            );
          })()}
        </Modal>
      )}
    </PageContainer>
  );
}
